/**
 * DeliverySection — mode-driven delivery block shared by the catalogue item
 * page and the merchandise request. One form for every delivery conflated
 * quite different real-world journeys (ship, collect, multi-destination,
 * mail, hold); this section splits them at the top via a mode pill so each
 * sub-form holds only the fields its mode needs. It reports a flat
 * `DeliverySelection` (shared-core `delivery-types`).
 *
 * Two address books (`addressBook`):
 *   - `tharstern_replica` (default, the merchandise request): the synced
 *     Tharstern address book, searched client-side. The request sends only
 *     the mode; the address is there to help the merchandise team.
 *   - `customer_spine` (the catalogue item page): the customer's CIS
 *     addresses, searched and paged server-side; a capture creates a real CIS
 *     address at once. The selection names the destination by its CIS
 *     address id (`deliveryAddressId`).
 * Either way the address is shown as a summary with a Change button that
 * opens the book in a modal (`DeliveryAddressPicker`).
 *
 * Pricing (order to invoice doc 104 §9.1): given the price preview's
 * `pricedDelivery`, Ship offers the "Courier service" list (the priced
 * courier options, or an agreed price with its reason), weight and parcels
 * while an item has no weight or packing, and the "Delivery" total row;
 * Collect lists our collection points and who is collecting. Multi sends the
 * line to several addresses (doc 106 §3): each row reads its own delivery at
 * its default service, chosen per address at checkout. Without
 * `pricedDelivery` (the merchandise request, Q9) there is no courier picker.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type { CatalogueDeliveryAddress } from "@shared-core/data/api-types";
import type { CatalogueDelivery } from "@shared-core/data/catalogue-types";
import type {
  DeliveryMode,
  DeliverySelection,
} from "@shared-core/data/delivery-types";
import type { CourierInputErrors } from "@shared-core/domain/delivery-field-errors";
import {
  isMissingWeightOrPacking,
  penceOfPounds,
  splitAllocated,
} from "@shared-core/domain/delivery-selection";
import {
  NOT_YET_PRICED,
  includedDeliveryReading,
} from "@shared-core/domain/checkout-delivery";
import { formatQuotedPrice } from "@shared-core/domain/formatters";

import { Card } from "../../primitives/card";
import { Badge } from "../../primitives/badge";
import { Input } from "../../primitives/input";
import { Tabs, TabsContent } from "../../primitives/tabs";
import { DeliveryModePills } from "./DeliveryModePills";
import { DeliveryAddressPicker } from "./DeliveryAddressPicker";
import type { AddressCapture, FetchAddressPage } from "./DeliveryAddressDialog";
import {
  filterPickerAddresses,
  replicaToPickerAddress,
  spineToPickerAddress,
  type PickerAddress,
} from "./pickerAddress";
import {
  AgreedPriceFields,
  CourierServiceList,
  type CourierChoice,
} from "./CourierServiceList";
import {
  CollectionFields,
  type CollectionPointsState,
} from "./CollectionFields";
import { Loader2, Plus, Truck, X } from "lucide-react";
import { V1TrialBadge } from "./V1TrialBadge";
import { currentTime } from "@shared-core/domain/current-time";

// =====================================================================
// Types
// =====================================================================

// DeliveryMode is the canonical shared type — re-exported here so
// consumers need no second import.
export type { DeliveryMode, CourierInputErrors };

/** Which address book the Ship / Multi destination pickers read — see the
 *  module comment. */
export type DeliveryAddressBookSource = "tharstern_replica" | "customer_spine";

export interface DeliverySectionProps {
  /** Tharstern customer code whose address book to search. Empty until
   *  the operator picks a customer. */
  customerCode: string;
  /** Defaults to `tharstern_replica`. */
  addressBook?: DeliveryAddressBookSource;
  /** The server's refusal of the chosen destination (a field error on the
   *  submit's `delivery.delivery_address_id`), bound next to the picker. */
  destinationError?: string | null;
  /** The server's refusal of the collection point (a field error on
   *  `delivery.collection_point_address_id`), bound next to the points. */
  collectionPointError?: string | null;
  /** The server's refusals of the courier choice (field errors on
   *  `delivery.service_level`, `.agreed_delivery_price_pence`,
   *  `.agreed_delivery_reason`, `.weight_kg`, `.parcels`), each bound next
   *  to its input. */
  courierErrors?: CourierInputErrors;
  /** The site making the goods: Hold defaults to holding them there. */
  productionSite: string;
  /** Reports the current selection whenever it changes. */
  onChange: (selection: DeliverySelection) => void;
  /** Disable all inputs (e.g. while the request is submitting). */
  disabled?: boolean;
  /** The delivery modes offered. Defaults to Ship, Multi, Collect and Hold. */
  allowedModes?: readonly DeliveryMode[];
  /** The price preview's delivery for this line: the courier list, the
   *  total row and Collect's points read it. Null while it is being priced.
   *  Absent where delivery is not priced (the merchandise request): no
   *  courier picker, no total row. */
  pricedDelivery?: CatalogueDelivery | null;
  /** Who delivers when the price includes delivery (the chosen supplier
   *  option's name): "Next day · PrintCo delivers". */
  deliveringSupplier?: string | null;
  /** The price preview's warnings for each route not offered because its
   *  delivery isn't priced (order to invoice doc 107). Each is shown with
   *  "Agreed price" and "Reason", which make the route an option, even while
   *  the chosen route's price includes delivery. */
  deliveryNotPricedWarnings?: readonly string[];
  /** The line's quantity: Multi-destination's rows add up to it
   *  ("Allocated {x} of {y}", order to invoice doc 106 §3). */
  quantity?: number;
  /** Multi-destination: each row's delivery at its default service, from the
   *  price preview, in the rows' order. */
  destinationDeliveries?: readonly CatalogueDelivery[];
  /** The server's refusals of the rows: the split as a whole
   *  (`delivery.delivery_lines`) and each row's address, by row. */
  deliveryLinesError?: string | null;
  deliveryLineErrors?: Readonly<Record<number, string>>;
  /** *Change delivery…* (order to invoice doc 106 §9.4): opens on the
   *  line's deliveries as they are, one as Ship, several as
   *  Multi-destination. A delivery goods have gone to is locked: it keeps
   *  its address, cannot be removed, and takes at least what has gone. */
  opening?: readonly DeliveryOpening[];
  /** One's delivery-allocation widget: the address modal's "Capture a new
   *  address" runs the create-address command in the command shell instead of
   *  this section's own capture form, resolving with the address created (null
   *  when cancelled). Absent everywhere else, so the built-in capture is
   *  unchanged. */
  captureAddress?: () => Promise<CatalogueDeliveryAddress | null>;
}

/** One delivery a line has now, as *Change delivery…* opens on it. */
export interface DeliveryOpening {
  addressId: string;
  label: string;
  postcode: string | null;
  quantity: number;
  /** What has already gone to it: the row is locked when this is above 0. */
  goneQuantity: number;
  /** Orders' words for the lock ("500 despatched: at least 500."); null when nothing has gone. */
  lockedNote: string | null;
}

const DEFAULT_MODES: readonly DeliveryMode[] = [
  "ship",
  "multi",
  "collect",
  "hold",
];

// Production sites — the sites Hold can keep the goods at.
const PRODUCTION_SITES = [
  "Exeter - Oak House",
  "Buckingham - Osier Way",
  "Buckingham - Display",
  "Plymouth - Sisna Park Road",
  "Buckingham - 63",
];

// Postal-service options for Mailing mode. Pulled from the
// note-pattern analysis's `mailing:postal_service_spec` category —
// these are the values operators most often retype today.
const POSTAL_SERVICES = [
  "Royal Mail 1st Class",
  "Royal Mail 2nd Class",
  "Royal Mail Door Drop",
  "Royal Mail Unsorted OCR",
  "DSA",
  "Other",
];

// =====================================================================
// Address helpers
// =====================================================================

// Spine addresses page through the picker's modal this many at a time.
const SPINE_PAGE_SIZE = 25;

// The server's own words when the spine's address book can't be read
// (403 outside the portfolio, 404 no such customer yet).
function spineBookFailure(err: unknown): string {
  if (err instanceof ApiError && err.status < 500) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
  }
  return "This customer's delivery addresses could not be loaded.";
}

type SpineBookState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; cisCustomerCode: string; total: number }
  | { status: "error"; message: string };

// One row in Multi-destination mode: an address and how many go there
// (order to invoice doc 106 §3). Each address is its own delivery, its
// service chosen at checkout.
interface MultiRow {
  /** Synthetic stable id — just for React keys; not surfaced. */
  id: string;
  destination: PickerAddress | null;
  qty: number;
  /** What has already gone to it (*Change delivery…*): it keeps its address and at least this. */
  goneQuantity?: number;
  lockedNote?: string | null;
}

const NO_DESTINATION_DELIVERIES: readonly CatalogueDelivery[] = [];
const NO_ROW_ERRORS: Readonly<Record<number, string>> = {};
const NO_WARNINGS: readonly string[] = [];

// =====================================================================
// Main component
// =====================================================================

export function DeliverySection({
  customerCode,
  addressBook = "tharstern_replica",
  destinationError = null,
  collectionPointError = null,
  courierErrors = {},
  productionSite,
  onChange,
  disabled = false,
  allowedModes = DEFAULT_MODES,
  pricedDelivery,
  deliveringSupplier = null,
  deliveryNotPricedWarnings = NO_WARNINGS,
  quantity,
  destinationDeliveries = NO_DESTINATION_DELIVERIES,
  deliveryLinesError = null,
  deliveryLineErrors = NO_ROW_ERRORS,
  opening,
  captureAddress,
}: DeliverySectionProps) {
  const adapter = useDataAdapter();
  const isSpine = addressBook === "customer_spine";
  const pricesDelivery = pricedDelivery !== undefined;

  // ----- Mode -----
  const opensSplit =
    (opening?.length ?? 0) > 1 && allowedModes.includes("multi");
  const [mode, setMode] = useState<DeliveryMode>(
    opensSplit ? "multi" : (allowedModes[0] ?? "ship"),
  );

  // ----- Ship / Multi: shared destination state -----
  // The primary destination feeds the Ship sub-form; Multi's first row is
  // its lead destination.
  // A line's one delivery opens on its own address, not the book's default.
  const [openedOn] = useState<PickerAddress | null>(() =>
    opening?.length === 1 ? openingAddress(opening[0]!) : null,
  );
  const [destination, setDestination] = useState<PickerAddress | null>(
    openedOn,
  );
  // Ship's one delivery when goods have gone to it: it keeps its address.
  const shipGone = opening?.length === 1 ? opening[0]!.goneQuantity : 0;
  const shipLockedNote = opening?.length === 1 ? opening[0]!.lockedNote : null;

  // ----- Ship / Multi: the courier choice (priced surfaces only) -----
  const [courierChoice, setCourierChoice] = useState<CourierChoice>({
    kind: "default",
  });
  const [agreedPrice, setAgreedPrice] = useState("");
  const [agreedReason, setAgreedReason] = useState("");
  // "Clear agreed price": back to the default, forgetting the price and its reason.
  const clearAgreedPrice = () => {
    setCourierChoice({ kind: "default" });
    setAgreedPrice("");
    setAgreedReason("");
  };
  const [weightKg, setWeightKg] = useState("");
  const [parcels, setParcels] = useState("");
  // The last priced delivery, kept on screen while the next is priced so the
  // list does not empty under the CSR's pointer at every choice.
  const [lastDelivery, setLastDelivery] = useState<CatalogueDelivery | null>(
    null,
  );
  useEffect(() => {
    if (pricedDelivery) setLastDelivery(pricedDelivery);
  }, [pricedDelivery]);
  const delivery = pricesDelivery ? (pricedDelivery ?? lastDelivery) : null;

  // ----- Multi mode extras -----
  // Additional destinations beyond the first, captured here for review.
  const [multiRows, setMultiRows] = useState<MultiRow[]>(() =>
    opensSplit ? opening!.map(openingRow) : [],
  );

  // ----- Spine book: its first read -----
  // The first page names the customer's CIS code (capture needs it) and the
  // default address, which is chosen to begin with. Later pages and searches
  // are read by the picker's modal.
  const [spineBook, setSpineBook] = useState<SpineBookState>({
    status: "idle",
  });
  const [spineReloadKey, setSpineReloadKey] = useState(0);

  // The built-in capture needs the customer's CIS code, which the book's
  // first read supplies; until then there is nowhere to create the address.
  // The delivery-allocation widget runs its own capture (`captureAddress`).
  const capture = useMemo<AddressCapture | null>(() => {
    if (!isSpine) return null;
    if (captureAddress) {
      return {
        kind: "host",
        run: async () => {
          const created = await captureAddress();
          return created ? spineToPickerAddress(created) : null;
        },
      };
    }
    return spineBook.status === "ready"
      ? { kind: "form", cisCustomerCode: spineBook.cisCustomerCode }
      : null;
  }, [isSpine, captureAddress, spineBook]);

  // ----- Collect mode (priced surfaces): our collection points -----
  const [collectionPoints, setCollectionPoints] =
    useState<CollectionPointsState>({ status: "idle" });
  // Bumped by "Try again" after a failed read.
  const [collectionPointsReadKey, setCollectionPointsReadKey] = useState(0);
  const [collectionPointId, setCollectionPointId] = useState<string | null>(
    null,
  );
  const [collectorName, setCollectorName] = useState("");
  const [collectorPhone, setCollectorPhone] = useState("");
  // Read the first time Collect is opened on a priced surface, and again on "Try again" after a failed read.
  const needsCollectionPoints = pricesDelivery && mode === "collect";
  const collectionPointsReadFor = useRef<number | null>(null);
  useEffect(() => {
    if (
      !needsCollectionPoints ||
      collectionPointsReadFor.current === collectionPointsReadKey
    )
      return;
    collectionPointsReadFor.current = collectionPointsReadKey;
    setCollectionPoints({ status: "loading" });
    adapter
      .listCollectionPoints()
      .then((points) => {
        setCollectionPoints({ status: "ready", points });
        // The only point there is needs no choosing.
        if (points.length === 1) setCollectionPointId(points[0]!.id);
      })
      .catch(() => setCollectionPoints({ status: "error" }));
  }, [adapter, needsCollectionPoints, collectionPointsReadKey]);

  // ----- Mail mode -----
  const [postalService, setPostalService] = useState<string>(
    POSTAL_SERVICES[1],
  );
  const [mailingHouse, setMailingHouse] = useState<"in-house" | "outsourced">(
    "in-house",
  );
  const [outsourcedSupplier, setOutsourcedSupplier] = useState("");
  const [broadcastDate, setBroadcastDate] = useState("");
  const [mailingComponentText, setMailingComponentText] = useState("");

  // ----- Hold mode (= "print to stock") -----
  const [holdSite, setHoldSite] = useState(productionSite);
  const [holdTrigger, setHoldTrigger] = useState("");

  // Keep the production-site default in sync if the caller's site changes.
  useEffect(() => {
    setHoldSite((prev) => (prev ? prev : productionSite));
  }, [productionSite]);

  // Ship's destination; in Multi mode, whether any row has an address (each
  // row is its own delivery, priced and sent).
  const leadDestinationId =
    mode === "multi"
      ? (multiRows.find((r) => r.destination)?.destination?.id ?? null)
      : (destination?.id ?? null);

  // A new destination is priced afresh: its couriers, and what was entered
  // for the last one, need not apply to it.
  useEffect(() => {
    setCourierChoice({ kind: "default" });
    setWeightKg("");
    setParcels("");
    setLastDelivery(null);
  }, [leadDestinationId]);

  // Spine book: the first page (default first), auto-picking the default.
  useEffect(() => {
    if (!isSpine) return;
    if (!customerCode) {
      setSpineBook({ status: "idle" });
      setDestination(null);
      return;
    }
    let cancelled = false;
    setSpineBook({ status: "loading" });
    adapter
      .listCatalogueDeliveryAddresses(customerCode, {
        pageSize: SPINE_PAGE_SIZE,
      })
      .then((book) => {
        if (cancelled) return;
        setSpineBook({
          status: "ready",
          cisCustomerCode: book.cis_customer_code,
          total: book.addresses.total,
        });
        const fallback = book.addresses.items.find(
          (a) => a.is_default_delivery,
        );
        setDestination(
          openedOn ?? (fallback ? spineToPickerAddress(fallback) : null),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setDestination(null);
        setSpineBook({ status: "error", message: spineBookFailure(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, customerCode, isSpine, openedOn, spineReloadKey]);

  // Replica book: the whole book is read once, auto-picking the default; the
  // picker's modal searches that same read client-side.
  const replicaBookRead = useRef<Promise<PickerAddress[]> | null>(null);
  useEffect(() => {
    if (isSpine) return;
    if (!customerCode) {
      replicaBookRead.current = null;
      setDestination(null);
      return;
    }
    let cancelled = false;
    const read = adapter
      .searchDeliveryAddresses(customerCode)
      .then((book) => book.map(replicaToPickerAddress));
    replicaBookRead.current = read;
    read
      .then((book) => {
        if (cancelled) return;
        // Only a flagged-default address auto-selects.
        setDestination(book.find((a) => a.isDefault) ?? null);
      })
      .catch(() => {
        // Defensive: a failed address-book load must not block the form.
        if (!cancelled) setDestination(null);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, customerCode, isSpine]);

  // Multi mode opens with two rows: the Ship destination taking the whole
  // quantity, and an empty one to send some elsewhere.
  useEffect(() => {
    if (mode !== "multi" || multiRows.length > 0) return;
    setMultiRows([
      {
        id: newRowId(),
        destination,
        qty: quantity ?? 0,
        goneQuantity: shipGone || undefined,
        lockedNote: shipLockedNote,
      },
      emptyRow(),
    ]);
    // destination is intentionally captured at seed time — we don't want the
    // seed to re-run when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ----- Report the current selection up via onChange (mode-aware) -----
  const selection = useMemo(
    () =>
      computeSelection({
        isSpine,
        mode,
        leadDestinationId,
        multiRows,
        courierChoice,
        agreedPrice,
        agreedReason,
        weightKg,
        parcels,
        collectionPointId,
        collectorName,
        collectorPhone,
      }),
    [
      isSpine,
      mode,
      leadDestinationId,
      multiRows,
      courierChoice,
      agreedPrice,
      agreedReason,
      weightKg,
      parcels,
      collectionPointId,
      collectorName,
      collectorPhone,
    ],
  );
  useEffect(() => {
    onChange(selection);
    // onChange is intentionally left out — it would re-fire every
    // parent render and we don't want a feedback loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  // One page of the picker modal's addresses. The replica book is read once
  // (above) and searched here; the spine book is searched and paged by the
  // server.
  const fetchAddressPage = useCallback<FetchAddressPage>(
    async (query, page) => {
      if (!customerCode) return { addresses: [], hasMore: false };
      if (!isSpine) {
        const book = (await replicaBookRead.current) ?? [];
        return {
          addresses: filterPickerAddresses(book, query),
          hasMore: false,
        };
      }
      const book = await adapter.listCatalogueDeliveryAddresses(customerCode, {
        q: query || undefined,
        page,
        pageSize: SPINE_PAGE_SIZE,
      });
      return {
        addresses: book.addresses.items.map(spineToPickerAddress),
        hasMore: page < book.addresses.total_pages,
      };
    },
    [adapter, customerCode, isSpine],
  );

  // ----- Multi-row helpers -----
  const addMultiRow = () => setMultiRows((prev) => [...prev, emptyRow()]);
  // What the server adds up: the rows with an address (splitAllocated).
  const allocated = splitAllocated(selection);
  // Only the rows with an address and a quantity are sent, in order: the
  // preview's deliveries and the server's row refusals count those, so each
  // row reads the sent row it became.
  const sentPositions = useMemo(() => {
    let sent = 0;
    return multiRows.map((row) =>
      row.destination && row.qty > 0 ? sent++ : undefined,
    );
  }, [multiRows]);
  const removeMultiRow = (id: string) =>
    setMultiRows((prev) => prev.filter((r) => r.id !== id));
  const updateMultiRow = (id: string, patch: Partial<MultiRow>) =>
    setMultiRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    );
  const setMultiRowDestination = (id: string, destination: PickerAddress) =>
    updateMultiRow(id, { destination });

  // ----- The courier choice under the lead destination (priced only) -----
  const courierArea = () => {
    if (!pricesDelivery || !leadDestinationId || !delivery) return null;
    if (delivery.status === "address_required") return null;
    const warnings = deliveryNotPricedWarnings.length > 0 && (
      <ul
        className="space-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
        data-testid="delivery-not-priced-warnings"
      >
        {deliveryNotPricedWarnings.map((warning) => (
          <li key={warning}>{warning}</li>
        ))}
      </ul>
    );
    if (delivery.status === "included")
      return (
        <div className="space-y-2">
          <div className="space-y-1">
            <p className="text-sm font-medium">Courier service</p>
            <p className="text-sm">
              {includedDeliveryReading(
                delivery.serviceLevel
                  ? (delivery.options.find(
                      (o) => o.serviceLevel === delivery.serviceLevel,
                    )?.serviceLevelLabel ?? null)
                  : null,
                deliveringSupplier,
              )}
            </p>
          </div>
          {warnings}
          {/* The chosen route's price includes delivery, so the agreed price is for a route withheld for its
              delivery (doc 107): typing it makes that route an option. Kept on screen while it is agreed. */}
          {(deliveryNotPricedWarnings.length > 0 ||
            courierChoice.kind === "agreed") && (
            <AgreedPriceFields
              agreedPrice={agreedPrice}
              onAgreedPriceChange={(value) => {
                setCourierChoice({ kind: "agreed" });
                setAgreedPrice(value);
              }}
              agreedReason={agreedReason}
              onAgreedReasonChange={(value) => {
                setCourierChoice({ kind: "agreed" });
                setAgreedReason(value);
              }}
              onClear={
                courierChoice.kind === "agreed" || agreedPrice || agreedReason
                  ? clearAgreedPrice
                  : undefined
              }
              agreedPriceError={courierErrors.agreedPrice ?? null}
              agreedReasonError={courierErrors.agreedReason ?? null}
              disabled={disabled}
            />
          )}
        </div>
      );
    const showsPackages =
      isMissingWeightOrPacking(delivery) || weightKg !== "" || parcels !== "";
    return (
      <>
        {warnings}
        <CourierServiceList
          delivery={delivery}
          choice={courierChoice}
          onChoose={(next) =>
            next.kind === "default"
              ? clearAgreedPrice()
              : setCourierChoice(next)
          }
          agreedPrice={agreedPrice}
          onAgreedPriceChange={setAgreedPrice}
          agreedReason={agreedReason}
          onAgreedReasonChange={setAgreedReason}
          choiceError={courierErrors.serviceLevel ?? null}
          agreedPriceError={courierErrors.agreedPrice ?? null}
          agreedReasonError={courierErrors.agreedReason ?? null}
          disabled={disabled}
        />
        {showsPackages && (
          <PackagesFields
            weightKg={weightKg}
            onWeightKgChange={setWeightKg}
            weightError={courierErrors.weight ?? null}
            parcels={parcels}
            onParcelsChange={setParcels}
            parcelsError={courierErrors.parcels ?? null}
            disabled={disabled}
          />
        )}
      </>
    );
  };

  // =====================================================================
  // Render
  // =====================================================================

  return (
    <Card className="p-4 sm:p-6 space-y-4 border-dashed border-2 border-border">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <Truck className="size-5 text-muted-foreground" />
        <h3 className="text-base font-semibold">Delivery</h3>
        <V1TrialBadge
          tone="trial"
          mapping="Delivery used to live in free-text notes. Here it's structured into modes — Ship, Collect, Multi-destination, Mailing or Hold — and each captures a different real-world journey."
        />
      </div>
      <p className="text-sm text-muted-foreground">
        Pick the mode that matches what&rsquo;s actually happening with this
        job. Ship is the standard case; the others are trial shapes for less
        common journeys — pick the one that fits and tell us what works.
      </p>

      {/* Mode pills. The Tabs root is still used for value-driven content
          show/hide. */}
      <Tabs value={mode} onValueChange={(v) => setMode(v as DeliveryMode)}>
        <DeliveryModePills
          current={mode}
          onChange={setMode}
          modes={allowedModes}
          disabled={disabled}
        />

        {/* ============ SHIP ============ */}
        <TabsContent value="ship" className="mt-4 space-y-4">
          <DestinationPicker
            customerCode={customerCode}
            value={destination}
            onChoose={setDestination}
            fetchPage={fetchAddressPage}
            capture={capture}
            spineBook={isSpine ? spineBook : null}
            onRetrySpineBook={() => setSpineReloadKey((k) => k + 1)}
            error={destinationError}
            disabled={disabled || shipGone > 0}
          />
          {shipGone > 0 && (
            <p className="text-sm text-muted-foreground">
              {shipLockedNote} Send the rest elsewhere with Multi-destination.
            </p>
          )}
          {mode === "ship" && courierArea()}
        </TabsContent>

        {/* ============ COLLECT ============ */}
        <TabsContent value="collect" className="mt-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Customer is collecting the goods themselves — no shipping, no
            courier service.
          </p>
          {pricesDelivery && (
            <CollectionFields
              state={collectionPoints}
              onRetry={() => setCollectionPointsReadKey((k) => k + 1)}
              pointId={collectionPointId}
              onPointChange={setCollectionPointId}
              pointError={collectionPointError}
              collectorName={collectorName}
              onCollectorNameChange={setCollectorName}
              collectorPhone={collectorPhone}
              onCollectorPhoneChange={setCollectorPhone}
              disabled={disabled}
            />
          )}
        </TabsContent>

        {/* ============ MULTI-DESTINATION ============ */}
        {/* One line to several addresses (order to invoice doc 106 §3): each
            row an address and how many go there, each its own delivery whose
            service is chosen at checkout. */}
        <TabsContent value="multi" className="mt-4 space-y-4">
          {!customerCode ? (
            <p className="text-sm text-muted-foreground">
              Pick a customer to choose delivery addresses.
            </p>
          ) : (
            <>
              <div className="rounded-md border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">
                        Address
                      </th>
                      <th className="text-left font-medium px-3 py-2 w-28">
                        Qty
                      </th>
                      {pricesDelivery && (
                        <th className="text-left font-medium px-3 py-2">
                          Courier service
                        </th>
                      )}
                      <th className="w-10 px-2 py-2" aria-hidden />
                    </tr>
                  </thead>
                  <tbody>
                    {multiRows.map((row, idx) => {
                      const sentAt = sentPositions[idx];
                      const rowError =
                        sentAt === undefined
                          ? undefined
                          : deliveryLineErrors[sentAt];
                      const rowErrorId = `multi-row-error-${row.id}`;
                      const gone = row.goneQuantity ?? 0;
                      const goneId = `multi-row-gone-${row.id}`;
                      return (
                        <tr
                          key={row.id}
                          className="border-t border-border align-top"
                        >
                          <td className="px-3 py-2 space-y-1">
                            <DeliveryAddressPicker
                              value={row.destination}
                              onChoose={(address) =>
                                setMultiRowDestination(row.id, address)
                              }
                              fetchPage={fetchAddressPage}
                              capture={capture}
                              changeLabel={`Change address for row ${idx + 1}`}
                              chooseLabel={`Choose an address for row ${idx + 1}`}
                              isInvalid={rowError !== undefined}
                              describedBy={rowError ? rowErrorId : undefined}
                              disabled={disabled || gone > 0}
                            />
                            {gone > 0 && (
                              <p
                                id={goneId}
                                className="text-xs text-muted-foreground"
                              >
                                {row.lockedNote}
                              </p>
                            )}
                            {rowError && (
                              <p
                                id={rowErrorId}
                                className="text-sm text-red-600"
                              >
                                {rowError}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              id={`multi-qty-${row.id}`}
                              type="number"
                              min={gone}
                              aria-label={`Quantity for address ${idx + 1}`}
                              aria-invalid={rowError ? true : undefined}
                              aria-describedby={
                                rowError
                                  ? rowErrorId
                                  : gone > 0
                                    ? goneId
                                    : undefined
                              }
                              value={row.qty || ""}
                              onChange={(e) =>
                                updateMultiRow(row.id, {
                                  qty: parseInt(e.target.value, 10) || 0,
                                })
                              }
                              disabled={disabled}
                              className="w-24 px-2 py-1 text-sm border border-border rounded text-right bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </td>
                          {pricesDelivery && (
                            <td className="px-3 py-2">
                              {rowDeliveryReading(
                                sentAt === undefined
                                  ? undefined
                                  : destinationDeliveries[sentAt],
                                deliveringSupplier,
                              ) ?? (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                          <td className="px-2 py-2 text-right">
                            <button
                              type="button"
                              aria-label={`Remove address ${idx + 1}`}
                              disabled={
                                disabled || multiRows.length === 1 || gone > 0
                              }
                              onClick={() => removeMultiRow(row.id)}
                              className="text-muted-foreground hover:text-[color:var(--color-destructive)] disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <X className="size-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={addMultiRow}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
                >
                  <Plus className="size-3.5" />
                  Add destination
                </button>
                {quantity != null && (
                  <p
                    className={`text-sm tabular-nums ${
                      allocated === quantity
                        ? "text-muted-foreground"
                        : "text-red-600"
                    }`}
                  >
                    {`Allocated ${allocated.toLocaleString("en-GB")} of ${quantity.toLocaleString("en-GB")}`}
                  </p>
                )}
              </div>
              {(deliveryLinesError ?? destinationError) && (
                <p className="text-sm text-red-600">
                  {deliveryLinesError ?? destinationError}
                </p>
              )}
            </>
          )}
        </TabsContent>

        {/* ============ MAILING ============ */}
        <TabsContent value="mailing" className="mt-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Mailing fulfilment isn&rsquo;t a courier shipment — destination is
            Royal Mail (or a mailing house) and the spec is a postal service,
            not a courier service.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="delivery-mail-postal"
                className="block text-sm font-medium mb-1"
              >
                Postal service
              </label>
              <select
                id="delivery-mail-postal"
                value={postalService}
                onChange={(e) => setPostalService(e.target.value)}
                disabled={disabled}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {POSTAL_SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="delivery-mail-broadcast"
                className="block text-sm font-medium mb-1"
              >
                Broadcast date{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </label>
              <input
                id="delivery-mail-broadcast"
                type="date"
                value={broadcastDate}
                onChange={(e) => setBroadcastDate(e.target.value)}
                disabled={disabled}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Mailing fulfilment</legend>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="delivery-mail-house"
                  value="in-house"
                  checked={mailingHouse === "in-house"}
                  onChange={() => setMailingHouse("in-house")}
                  disabled={disabled}
                />
                In-house
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="delivery-mail-house"
                  value="outsourced"
                  checked={mailingHouse === "outsourced"}
                  onChange={() => setMailingHouse("outsourced")}
                  disabled={disabled}
                />
                Outsourced to
              </label>
              <input
                type="text"
                value={outsourcedSupplier}
                onChange={(e) => setOutsourcedSupplier(e.target.value)}
                placeholder="supplier name"
                disabled={disabled || mailingHouse !== "outsourced"}
                className="flex-1 min-w-[12rem] px-2 py-1 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              />
            </div>
          </fieldset>
          <div>
            <label
              htmlFor="delivery-mail-component"
              className="block text-sm font-medium mb-1"
            >
              Mailing component notes{" "}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </label>
            <input
              id="delivery-mail-component"
              type="text"
              value={mailingComponentText}
              onChange={(e) => setMailingComponentText(e.target.value)}
              placeholder="e.g. envelopes, inserts…"
              disabled={disabled}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <CapturedLocallyBanner>
            Postal service and fulfilment are captured here for review; not sent
            yet — tell us what fields you actually need here.
          </CapturedLocallyBanner>
        </TabsContent>

        {/* ============ HOLD (= "print to stock") ============ */}
        <TabsContent value="hold" className="mt-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Print to stock — print now, hold the goods in our warehouse until
            they&rsquo;re released. No courier picked now.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="delivery-hold-site"
                className="block text-sm font-medium mb-1"
              >
                Hold at
              </label>
              <select
                id="delivery-hold-site"
                value={holdSite}
                onChange={(e) => setHoldSite(e.target.value)}
                disabled={disabled}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {PRODUCTION_SITES.map((site) => (
                  <option key={site} value={site}>
                    {site}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="delivery-hold-trigger"
                className="block text-sm font-medium mb-1"
              >
                Release trigger
              </label>
              <input
                id="delivery-hold-trigger"
                type="text"
                value={holdTrigger}
                onChange={(e) => setHoldTrigger(e.target.value)}
                placeholder="e.g. when customer calls / Q3 fulfilment"
                disabled={disabled}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <CapturedLocallyBanner>
            Where it is held and its release trigger are captured here for
            review; not sent yet.
          </CapturedLocallyBanner>
        </TabsContent>
      </Tabs>

      {pricesDelivery && (
        <DeliveryTotalRow
          mode={mode}
          hasDestination={leadDestinationId !== null}
          delivery={delivery}
          agreedPricePence={
            courierChoice.kind === "agreed" ? penceOfPounds(agreedPrice) : null
          }
          isAgreedPrice={mode === "ship" && courierChoice.kind === "agreed"}
        />
      )}
    </Card>
  );
}

// =====================================================================
// computeSelection — the DeliverySelection the section reports, from its
// mode and sub-form state. Only what belongs to the mode is set.
// =====================================================================

function computeSelection(args: {
  /** The picked destination is a CIS address id on the spine book, a
   *  Tharstern code on the replica book, which is not reported. */
  isSpine: boolean;
  mode: DeliveryMode;
  leadDestinationId: string | null;
  multiRows: readonly MultiRow[];
  courierChoice: CourierChoice;
  agreedPrice: string;
  agreedReason: string;
  weightKg: string;
  parcels: string;
  collectionPointId: string | null;
  collectorName: string;
  collectorPhone: string;
}): DeliverySelection {
  // A split line's service is chosen per address at checkout (doc 106 §3.4):
  // Multi reports only where each share goes.
  const ships = args.mode === "ship";
  const splits = args.mode === "multi";
  const collects = args.mode === "collect";
  const choice = ships ? args.courierChoice : ({ kind: "default" } as const);
  const isAgreedPrice = choice.kind === "agreed";
  return {
    mode: args.mode,
    deliveryLines:
      splits && args.isSpine
        ? args.multiRows.map((row) => ({
            deliveryAddressId: row.destination?.id ?? null,
            quantity: row.qty || 0,
          }))
        : [],
    deliveryAddressId: ships && args.isSpine ? args.leadDestinationId : null,
    serviceLevel: choice.kind === "option" ? choice.serviceLevel : null,
    courierSupplierId:
      choice.kind === "option" ? choice.courierSupplierId : null,
    isAgreedPrice,
    agreedDeliveryPricePence: isAgreedPrice
      ? penceOfPounds(args.agreedPrice)
      : null,
    agreedDeliveryReason: isAgreedPrice ? args.agreedReason.trim() : null,
    weightKg: ships ? positiveNumber(args.weightKg) : null,
    parcels: ships ? wholeParcels(args.parcels) : null,
    collectionPointAddressId: collects ? args.collectionPointId : null,
    recipientName: collects ? args.collectorName.trim() || null : null,
    recipientPhone: collects ? args.collectorPhone.trim() || null : null,
  };
}

function newRowId(): string {
  return `row-${currentTime().getTime()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** A delivery the line has now, as the picker shows it until the book lists it. */
function openingAddress(delivery: DeliveryOpening): PickerAddress {
  return {
    id: delivery.addressId,
    name: delivery.label,
    address: "",
    postcode: delivery.postcode ?? "",
    country: "",
    isDefault: false,
    isCisOnly: false,
  };
}

function openingRow(delivery: DeliveryOpening): MultiRow {
  return {
    id: newRowId(),
    destination: openingAddress(delivery),
    qty: delivery.quantity,
    goneQuantity: delivery.goneQuantity || undefined,
    lockedNote: delivery.lockedNote,
  };
}

function emptyRow(): MultiRow {
  return {
    id: newRowId(),
    destination: null,
    qty: 0,
  };
}

/** A Multi row's delivery as the preview priced it: "{level} · £X" at its
 *  default service, "Included", or "Not yet priced". */
function rowDeliveryReading(
  delivery: CatalogueDelivery | undefined,
  deliveringSupplier: string | null,
): string | null {
  if (!delivery || delivery.status === "address_required") return null;
  if (delivery.status === "included")
    return includedDeliveryReading(null, deliveringSupplier);
  if (delivery.status === "not_yet_priced") return NOT_YET_PRICED;
  const level = delivery.options.find(
    (o) => o.serviceLevel === delivery.serviceLevel,
  )?.serviceLevelLabel;
  const price =
    delivery.deliveryPricePence != null
      ? formatQuotedPrice(delivery.deliveryPricePence)
      : null;
  return [level, price].filter(Boolean).join(" · ") || null;
}

function positiveNumber(text: string): number | null {
  const value = Number(text.trim());
  return text.trim() && Number.isFinite(value) && value > 0 ? value : null;
}

function wholeParcels(text: string): number | null {
  const value = Number(text.trim());
  return text.trim() && Number.isInteger(value) && value >= 1 ? value : null;
}

// =====================================================================
// The section's total row: "Delivery" and its price, "Included",
// "Collection", or "Not yet priced" with the server's reason.
// =====================================================================

function DeliveryTotalRow({
  mode,
  hasDestination,
  delivery,
  agreedPricePence,
  isAgreedPrice,
}: {
  mode: DeliveryMode;
  hasDestination: boolean;
  delivery: CatalogueDelivery | null;
  agreedPricePence: number | null;
  isAgreedPrice: boolean;
}) {
  const ships = mode === "ship" || mode === "multi";
  if (!ships && mode !== "collect") return null;
  if (ships && !hasDestination) return null;
  if (delivery?.status === "address_required") return null;
  const priced = (pence: number | null) =>
    pence == null ? "—" : `+${formatQuotedPrice(pence)}`;
  const value = !delivery
    ? "—"
    : mode === "collect"
      ? delivery.status === "collection"
        ? "Collection"
        : "—"
      : isAgreedPrice
        ? priced(agreedPricePence)
        : delivery.status === "included"
          ? "Included"
          : delivery.status === "not_yet_priced"
            ? NOT_YET_PRICED
            : priced(delivery.deliveryPricePence);
  const note =
    ships && !isAgreedPrice && delivery?.status === "not_yet_priced"
      ? delivery.notPricedMessage
      : null;
  return (
    <div className="space-y-1 border-t border-border pt-3">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span>Delivery</span>
        <span className="font-medium tabular-nums">{value}</span>
      </div>
      {note && <p className="text-sm text-muted-foreground">{note}</p>}
    </div>
  );
}

// =====================================================================
// Weight and parcels — staff-entered packages for a delivery not yet
// priced because an item has no weight or packing (doc 104 Q6).
// =====================================================================

function PackagesFields({
  weightKg,
  onWeightKgChange,
  weightError,
  parcels,
  onParcelsChange,
  parcelsError,
  disabled,
}: {
  weightKg: string;
  onWeightKgChange: (value: string) => void;
  /** The server's refusal of the weight (`delivery.weight_kg`), beside it. */
  weightError: string | null;
  parcels: string;
  onParcelsChange: (value: string) => void;
  /** The server's refusal of the parcels (`delivery.parcels`), beside them. */
  parcelsError: string | null;
  disabled: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:max-w-sm">
      <div className="space-y-1">
        <label
          htmlFor="delivery-weight-kg"
          className="block text-sm font-medium"
        >
          Weight (kg)
        </label>
        <Input
          id="delivery-weight-kg"
          inputMode="decimal"
          value={weightKg}
          onChange={(e) => onWeightKgChange(e.target.value)}
          disabled={disabled}
          className="tabular-nums"
          aria-invalid={weightError ? true : undefined}
          aria-describedby={weightError ? "delivery-weight-error" : undefined}
        />
        {weightError && (
          <p id="delivery-weight-error" className="text-sm text-red-600">
            {weightError}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <label htmlFor="delivery-parcels" className="block text-sm font-medium">
          Parcels
        </label>
        <Input
          id="delivery-parcels"
          inputMode="numeric"
          value={parcels}
          onChange={(e) => onParcelsChange(e.target.value)}
          disabled={disabled}
          className="tabular-nums"
          aria-invalid={parcelsError ? true : undefined}
          aria-describedby={parcelsError ? "delivery-parcels-error" : undefined}
        />
        {parcelsError && (
          <p id="delivery-parcels-error" className="text-sm text-red-600">
            {parcelsError}
          </p>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Destination picker — Ship's one destination: the summary of the chosen
// address with its Change button, and the states before there is a book
// to choose from.
// =====================================================================

function DestinationPicker(props: {
  customerCode: string;
  value: PickerAddress | null;
  onChoose: (address: PickerAddress) => void;
  fetchPage: FetchAddressPage;
  capture: AddressCapture | null;
  /** The spine book's first read, or null for the replica book. */
  spineBook: SpineBookState | null;
  onRetrySpineBook: () => void;
  /** The server's refusal of the chosen destination. */
  error: string | null;
  disabled: boolean;
  label?: string;
}) {
  const {
    customerCode,
    value,
    onChoose,
    fetchPage,
    capture,
    spineBook,
    onRetrySpineBook,
    error,
    disabled,
    label = "Destination address",
  } = props;
  const errorId = "delivery-destination-error";

  if (customerCode && spineBook?.status === "loading") {
    return (
      <div className="space-y-1.5">
        <p className="block text-sm font-medium">{label}</p>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading the customer&rsquo;s delivery addresses…
        </p>
      </div>
    );
  }
  if (customerCode && spineBook?.status === "error") {
    return (
      <div className="space-y-1.5">
        <p className="block text-sm font-medium">{label}</p>
        <div
          role="alert"
          className="flex flex-wrap items-center gap-2 text-sm text-red-700"
        >
          <span>{spineBook.message}</span>
          <button
            type="button"
            onClick={onRetrySpineBook}
            disabled={disabled}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (!customerCode) {
    return (
      <div className="space-y-1.5">
        <p className="block text-sm font-medium">{label}</p>
        <p className="text-sm text-muted-foreground">
          Pick a customer to choose a delivery address.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {spineBook?.status === "ready" && spineBook.total === 0 && !value && (
        <p className="text-sm text-muted-foreground">
          This customer has no delivery addresses yet.
        </p>
      )}
      <DeliveryAddressPicker
        label={label}
        value={value}
        onChoose={onChoose}
        fetchPage={fetchPage}
        capture={capture}
        changeLabel="Change delivery destination address"
        isInvalid={error !== null}
        describedBy={error ? errorId : undefined}
        disabled={disabled}
      />
      {error && (
        <p id={errorId} className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

// =====================================================================
// CapturedLocallyBanner — small dashed-border note for the non-Ship
// modes, making it explicit that the v1 illustration captures intent
// here but doesn't fully wire to Tharstern yet.
// =====================================================================

function CapturedLocallyBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-t border-dashed border-border pt-3 text-xs text-muted-foreground flex items-start gap-2">
      <Badge variant="neutral">v1 trial · captured locally</Badge>
      <span className="flex-1">{children}</span>
    </div>
  );
}
